<template>
  <o-dialog
    ref="dialog"
    :title="title"
    :info="info"
    :options="[
      { text: $t('general.back'), type: 'cancel' },
      { text: $t('components.reservation.actions.cancel.confirm'), type: 'confirm', callback: confirm },
    ]"
    :portal="portal"
    :valid="validate"
  >
    <div class="pt-4 text-left">
      <o-form ref="form">
        <o-field-group v-if="!reservation?.isGroup && cancellationTypeOptions.length" :label="$t('components.reservation.actions.cancel.reason')" class="mb-4 block">
          <select-field v-model="cancellationType" :options="cancellationTypeOptions" />
        </o-field-group>

        <o-field-group v-slot="{ status }" :label="$t('components.reservation.actions.cancel.notes')" :name="$t('components.reservation.actions.cancel.notes')" :rules="isNoteRequired ? 'required' : ''">
          <text-area-field v-model="cancellationNote" :status="status" />
        </o-field-group>

        <o-divider />

        <o-field-group v-if="isRecurring">
          <checkbox-field v-model="future" label-class="text-sm ml-3" :label="$t('components.reservation.actions.cancel.cancelFuture')" :info="$t('components.reservation.actions.cancel.cancelFutureInfo')" />
        </o-field-group>

        <o-field-group>
          <checkbox-field v-model="sendEmail" label-class="text-sm ml-3" :label="$t('general.sendEmail')" :info="$t('components.reservation.actions.cancel.sendEmailInfo')" />
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import {
  ODivider,
} from '@/components/elements'

import {
  CheckboxField,
  SelectField,
  TextAreaField,
} from '@/components/fields'

interface Props {
  reservation: Record<string, any>
  cancellationTypes: any[]
  portal?: string | null
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  portal: null,
  loading: false,
})

const emit = defineEmits<{
  'update:loading': [value: boolean]
  'update:reservation': [value: any]
  'update': []
}>()

const { $api, $notification, $bus, $auth, $i18n } = useNuxtApp()

const dialog = ref<any>(null)
const form = ref<any>(null)

const future = ref(false)
const sendEmail = ref(true)
const cancellationType = ref<any>(null)
const cancellationNote = ref('')

const title = computed(() => {
  return props.reservation?.isGroup
    ? $i18n.t('components.reservation.actions.cancel.areYouSureClass')
    : $i18n.t('components.reservation.actions.cancel.areYouSure')
})

const info = computed(() => {
  return props.reservation?.isGroup
    ? $i18n.t('components.reservation.actions.cancel.cancelViaCustomersTab')
    : null
})

const isRecurring = computed(() => {
  return !!props.reservation?.isRecurring
})

const isNoteRequired = computed(() => {
  return !!(cancellationType.value && cancellationType.value.noteRequired)
})

const cancellationTypeOptions = computed(() => {
  const types = props.cancellationTypes

  return types.map((type) => {
    return { label: type.name, value: type }
  })
})

function open() {
  sendEmail.value = $auth.info?.location?.sendInternalReservationCancelNotifications
  dialog.value.open()
}

async function confirm() {
  emit('update:loading', true)

  const futureVal = future.value
  const sendEmailVal = sendEmail.value
  const cancellationTypeVal = cancellationType.value
  const cancellationNoteVal = cancellationNote.value

  try {
    const isAttendee = props.reservation.idReservationCustomer != null
    const reservationId = props.reservation.idReservation?.toString()
    const reservationCustomerId = props.reservation.idReservationCustomer?.toString()
    const date = props.reservation.date?.integer ?? props.reservation.date
    const response = isAttendee ? await $api.reservationCustomer.cancel(reservationCustomerId, date, {
      future: futureVal,
      sendEmail: sendEmailVal,
      cancellationTypeId: cancellationTypeVal?.idReservationCancellationType,
      cancellationNote: cancellationNoteVal,
    }) : await $api.reservation.cancel(reservationId, date, {
      future: futureVal,
      sendEmail: sendEmailVal,
      cancellationTypeId: cancellationTypeVal?.idReservationCancellationType,
      cancellationNote: cancellationNoteVal,
    })
    const reservation = response.data

    if (reservation) {
      emit('update:reservation', reservation)
      emit('update:loading', false)
      emit('update')

      $bus.emit.updated(isAttendee ? 'ReservationCustomer' : 'Reservation', reservation)

      $notification.toast({
        title: $i18n.t('components.reservation.actions.cancel.success'),
        text: $i18n.t('components.reservation.actions.cancel.successInfo', { name: props.reservation.customer.displayName }),
      })

      return true
    }
  } catch (e) {
    console.log(e)
  }

  emit('update:loading', false)
}

function validate() {
  return form.value.validate()
}

defineExpose({ open })
</script>
